import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { useMatch } from "react-router-dom";
import Modal from "react-modal";
import * as _ from "lodash-es";
import IBreadcrumbs from "@models/IBreadcrumbs";
import ICatalog_ItemUI from "@models/ICatalog_ItemUI";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import OwActionBar from "@components/shared/OwActionBar/OwActionBar";
import OwButton from "@components/shared/OwButton/OwButton";
import { OwDataMapper } from "@components/shared/OwDataMapper/OwDataMapper";
import OwTable from "@components/shared/OwTable/OwTable";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import PaginationBar from "@components/shared/PaginationBar/PaginationBar";
import { SearchSection } from "@components/shared/SearchSection/SearchSection";
import { ConfirmDeleteOverlayFn } from "@components/shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { CatalogItemFilter } from "./CatalogItemFilter/CatalogItemFilter";
import CategorySidebar from "./CategorySidebar/CategorySidebar";
import PriceGroupSidebar from "./PriceGroupSidebar/PriceGroupSidebar";
import OrderRowConfig from "./OrderRowConfig/OrderRowConfig";
import InventoryRowConfig from "./InventoryRowConfig/InventoryRowConfig";
import { QBDItemSyncPriceGroupSelectionModal } from "./QBDItemSyncPriceGroupSelectionModal/QBDItemSyncPriceGroupSelectionModal";
import { QBDItemsSyncStatus } from "./QBDItemsSyncStatus/QBDItemsSyncStatus";
import { CloneCatalogItemModal } from "./CloneCatalogItemModal/CloneCatalogItemModal";
import { useCatalogOverview } from "@hooks/catalogs/useCatalogOverview";
import EndpointsEnum from "@models/EndpointsEnum";
import UserService from "@services/UserService";
import "./CatalogOverview.scss";
import UiContext from "@contexts/UiContext";
import SupplierContext from "@contexts/SupplierContext";
import { SocketContext } from "@contexts/SocketContext";
import UserContext from "@contexts/UserContext";
import IOnQBDPushItemsDTO from "@models/DTO/IOnQBDPushItemsDTO";
import IOnGetCatalogItemsDTO from "@models/DTO/IOnGetCatalogItemsDTO";
import IOnQBDCreateCatalogItemsDTO from "@models/DTO/IOnQBDCreateCatalogItemsDTO";
import IOnElasticSearchSyncCatalogDTO from "@models/DTO/IOnElasticSearchSyncCatalogDTO";
import IOnGetPriceGroupsDTO from "@models/DTO/IOnGetPriceGroupsDTO";
import { useCatalogSocketListeners } from "@hooks/catalogs/useCatalogSocketListeners";
import { useCatalogTableColumns } from "@hooks/catalogs/useCatalogTableColumns";
import { useCatalogActions } from "@hooks/catalogs/useCatalogActions";
import { CatalogContext, ICatalogContext, ICatalogFilterState } from "@contexts/CatalogContext";
import { ItemOptionsSidebar } from "../ItemOptionsSidebar/ItemOptionsSidebar";
import IOnGetCatalogDTO from "@models/DTO/IOnGetCatalogDTO";
import { ClickToCopy } from "@components/shared/ClickToCopy/ClickToCopy";
import ICatalogOnCreatePriceGroupDTO from "@models/DTO/ICatalogOnCreatePriceGroupDTO";
import IDeleteCatalogItemDTO from "@models/DTO/IDeleteCatalogItemDTO";
import ICatalogOnUpdatePriceGroupDTO from "@models/DTO/ICatalogOnUpdatePriceGroupDTO";
import ICatalog_Item from "@models/ICatalog_Item";
import { CreateCatalogItemSidebarWrapper } from "./CreateCatalogItemSidebarWrapper/CreateCatalogItemSidebarWrapper";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import { hasEditAccess } from "@services/ScreenOptionsService";
import ViewAccessService from "@services/ViewAccessService";

const CatalogOverviewFn = () => {
    const { userModel, supplierModel, socket, uiContext } = useAppContextNonNull();

    const match = useMatch("catalogs/:guid");

    const catalogGuid = match?.params?.guid || "";

    const { state, updateState } = useCatalogOverview({ catalogGuid });

    const isReadOnly = useMemo(() => {
        return !hasEditAccess(ScreenOptionsEnum.CATALOGS, userModel, supplierModel, ViewAccessService.Catalogs);
    }, [userModel, supplierModel]);

    const savePriceGroups = useCallback(
        (data: any) => {
            const updatePriceGroups: { guid: string; value: string }[] = [];
            const createPriceGroups: { value: string }[] = [];
            let isSaving = false;

            _.each(data, (priceGroup) => {
                if (priceGroup.isUpdated && !priceGroup.isNew) {
                    updatePriceGroups.push({
                        guid: priceGroup.guid,
                        value: priceGroup.value,
                    });
                } else if (priceGroup.isNew) {
                    createPriceGroups.push({ value: priceGroup.value });
                }
            });

            if (createPriceGroups.length) {
                const payload = {
                    userToken: userModel.token,
                    userGuid: userModel.guid,
                    supplierGuid: supplierModel.guid,
                    catalogGuid,
                    priceGroups: createPriceGroups,
                };

                socket.emit(EndpointsEnum.CatalogsCreatePriceGroup, payload);
                isSaving = true;
            }

            if (updatePriceGroups.length) {
                const payload = {
                    userToken: userModel.token,
                    userGuid: userModel.guid,
                    supplierGuid: supplierModel.guid,
                    catalogGuid,
                    priceGroups: updatePriceGroups,
                };

                socket.emit(EndpointsEnum.CatalogsUpdatePriceGroup, payload);
                isSaving = true;
            }

            if (isSaving) {
                uiContext.setActivityIndicator(true);
            }
        },
        [userModel.token, userModel.guid, supplierModel.guid, catalogGuid, socket, uiContext]
    );

    const deletePriceGroup = useCallback(
        (priceGroup: any) => {
            uiContext.setActivityIndicator(true);

            const payload = {
                userToken: userModel.token,
                userGuid: userModel.guid,
                supplierGuid: supplierModel.guid,
                priceGroupGuid: priceGroup.guid,
            };

            socket.emit(EndpointsEnum.CatalogsDeletePriceGroup, payload);
        },
        [userModel.token, userModel.guid, supplierModel.guid, socket, uiContext]
    );

    const saveCategories = useCallback((data: any) => {
        console.log("saveCategories", data);
    }, []);

    const closeModal = useCallback((refresh?: boolean) => {
        setModalIsOpen(false);
        setModalComponent(null);
    }, []);

    const openOrderRowConfigModal = useCallback(() => {
        updateState({ showOrderRowConfig: true });
    }, [updateState]);

    const openInventoryRowConfigModal = useCallback(() => {
        updateState({ showInventoryRowConfig: true });
    }, [updateState]);

    const openQuickBooksMapping = useCallback(() => {
        updateState({ qbdMapperVisible: true });
    }, [updateState]);

    const syncElasticSearch = useCallback(() => {
        const payload = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid,
        };

        socket.emit(EndpointsEnum.ElasticSearchSyncCatalog, payload);
        updateState({ syncElasticSearch: true });
    }, [userModel.guid, userModel.token, supplierModel.guid, catalogGuid, socket, updateState]);

    const openItemOptionsSidebar = useCallback(() => {
        if (state.catalogModel) {
            const component = <ItemOptionsSidebar catalogId={state.catalogModel.id} catalogGuid={catalogGuid} />;
            uiContext.setSidebarComponent(component);
            uiContext.toggleSidebar();
        }
    }, [state.catalogModel, catalogGuid, uiContext]);

    const openCatalogItemSidebar = useCallback(
        (catalog_item?: ICatalog_ItemUI, index?: number) => {
            const sidebarComp = (
                <CreateCatalogItemSidebarWrapper
                    catalogModel={state.catalogModel!}
                    catalogGuid={catalogGuid}
                    catalog_item={catalog_item}
                    priceGroups={state.priceGroups}
                    cancel={(refreshData?: boolean, item?: ICatalog_Item) => {
                        closeCatalogItemSidebar(refreshData, item, state.limit, state.offset);
                    }}
                    vendorSources={state.vendorSources}
                />
            );
            uiContext.setSidebarComponent(sidebarComp);
            uiContext.toggleSidebar();
        },
        [state.catalogModel, catalogGuid, state.priceGroups, state.vendorSources, state.limit, state.offset, uiContext]
    );

    const openPriceGroupsSidebar = () => {
        const sidebarComp = (
            <PriceGroupSidebar
                saveFn={savePriceGroups}
                priceGroups={state.priceGroups}
                cancel={uiContext.toggleSidebar}
                clearData={false}
                deleteFn={deletePriceGroup}
            />
        );
        uiContext.setSidebarComponent(sidebarComp);
        uiContext.toggleSidebar();
    };

    const openCategoriesSidebar = (isHomeCategories = false) => {
        if (!state.catalogModel) return;

        const sidebarComp = (
            <CategorySidebar
                notifyFn={uiContext.notifyFn}
                saveFn={saveCategories}
                categories={state.categories}
                cancel={uiContext.toggleSidebar}
                clearData={false}
                socket={socket}
                supplierModel={supplierModel}
                userModel={userModel}
                catalogModel={state.catalogModel}
                priceGroups={state.priceGroups}
                isHomeCategories={isHomeCategories}
            />
        );
        uiContext.setSidebarComponent(sidebarComp);
        uiContext.toggleSidebar();
    };

    const getCategoriesSidebarComponent = (isHomeCategories?: boolean) => {
        if (!state.catalogModel) {
            return null;
        }

        return (
            <CategorySidebar
                notifyFn={uiContext.notifyFn}
                saveFn={saveCategories}
                categories={state.categories}
                cancel={uiContext.toggleSidebar}
                clearData={false}
                socket={socket}
                supplierModel={supplierModel}
                userModel={userModel}
                catalogModel={state.catalogModel}
                priceGroups={state.priceGroups}
                isHomeCategories={isHomeCategories}
            />
        );
    };

    const openHomeCategoriesSidebar = () => {
        if (!state.catalogModel) {
            return null;
        }

        const sidebarComp = getCategoriesSidebarComponent(true);
        uiContext.setSidebarComponent(sidebarComp);
        uiContext.toggleSidebar();
    };

    const showQbdItemSyncPriceGroupSelectionModal = () => {
        if (!supplierModel.Supplier_Integration.qbd_has_price_levels && state.catalogModel) {
            const modalComponent = (
                <QBDItemSyncPriceGroupSelectionModal
                    closeModalFn={closeModal}
                    startFn={qbdAutoConnectItems}
                    socket={socket}
                    notifyFn={uiContext.notifyFn}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    priceGroups={state.priceGroups || []}
                />
            );

            setModalComponent(modalComponent);
            setModalIsOpen(true);
        } else {
            qbdAutoConnectItems();
        }
    };

    const qbdAutoConnectItems = (selectedPriceGroupGuid?: string) => {
        const payload: any = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid,
            updateExisting: true,
        };

        if (selectedPriceGroupGuid) {
            payload.priceGroupGuid = selectedPriceGroupGuid;
        }

        socket.emit(EndpointsEnum.QBDCreateCatalogItems, payload);
        updateState({ qbdItemSyncActive: true });
        setModalIsOpen(false);
        setModalComponent(null);
    };

    // Create catalog actions
    const { actions } = useCatalogActions({
        state,
        setState: updateState,
        catalogGuid,
        handlers: {
            openPriceGroupsSidebar,
            openCategoriesSidebar,
            openHomeCategoriesSidebar,
            openOrderRowConfigModal,
            openInventoryRowConfigModal,
            syncElasticSearch,
            openQuickBooksMapping,
            showQbdItemSyncPriceGroupSelectionModal,
            openItemOptionsSidebar,
        },
    });

    const [navItems, setNavItems] = useState<IBreadcrumbs[]>([]);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalComponent, setModalComponent] = useState<React.ReactNode>(null);
    const [qbdSelectableOptions, setQbdSelectableOptions] = useState<any[]>([]);
    const [contextState, setContextState] = useState<ICatalogContext>({
        activeProcess: false,
        queryString: state.queryString,
        setQueryString: (queryString: string) => {
            updateState({ queryString });
        },
        filterState: {},
        setFilterState: (filterState: ICatalogFilterState) => {
            setContextState((prev) => ({ ...prev, filterState }));
        },
    });

    const onGetCatalogItems = (data: IOnGetCatalogItemsDTO) => {
        const catalogItems = data.catalog_items.map((catalog_item) => {
            return {
                ...catalog_item,
                isSaved: true,
                editMode: false,
            } as ICatalog_ItemUI;
        });

        updateState({
            catalog_items: catalogItems,
            isLoading: false,
            isDataFetching: false,
            isInitialData: false,
            totalCatalogItems: data.totalCatalogItems,
        });
    };

    const onGetPriceGroups = (data: IOnGetPriceGroupsDTO) => {
        updateState({ priceGroups: data.priceGroups });
        uiContext.setActivityIndicator(false);
    };

    const onGetCatalog = (data: IOnGetCatalogDTO) => {
        updateState({ catalogModel: data.catalog });
    };

    const onElasticSearchSyncCatalog = (data: IOnElasticSearchSyncCatalogDTO) => {
        updateState({ syncElasticSearch: false });
    };

    const onQBDCreateCatalogItems = (data: IOnQBDCreateCatalogItemsDTO) => {
        updateState({ qbdItemSyncActive: false });
    };

    const onQBDPushItems = (data: IOnQBDPushItemsDTO) => {
        updateState({ qbdItemSyncActive: false });
    };

    const onCreatePriceGroup = (data: ICatalogOnCreatePriceGroupDTO) => {
        getPriceGroups();
        uiContext.toggleSidebar(false);
        uiContext.setSidebarComponent(null);
    };

    const onUpdatePriceGroup = (data: ICatalogOnUpdatePriceGroupDTO) => {
        getPriceGroups();
        uiContext.toggleSidebar(false);
        uiContext.setSidebarComponent(null);
    };

    // Create socket listeners
    const { getCatalog, getCatalogItems, getPriceGroups } = useCatalogSocketListeners({
        catalogGuid,
        setState: updateState,
        handlers: {
            onGetCatalogItems,
            onGetPriceGroups,
            onGetCatalog,
            onElasticSearchSyncCatalog,
            onQBDCreateCatalogItems,
            onQBDPushItems,
            onCreatePriceGroup,
            onUpdatePriceGroup,
        },
    });

    const closeCatalogItemSidebar = useCallback(
        (refreshData?: boolean, item?: ICatalog_Item, limit?: number, offset?: number) => {
            uiContext.toggleSidebar(false);
            uiContext.setSidebarComponent(null);

            if (refreshData) {
                getCatalogItems({ queryString: contextState.queryString, limit: limit || state.limit, offset: offset || state.offset });
            }
        },
        [contextState.queryString, state.limit, state.offset, getCatalogItems, uiContext]
    );

    const cloneItem = (row: ICatalog_ItemUI, index: number) => {
        const component = <CloneCatalogItemModal item={row} closeModal={closeModal} />;
        setModalComponent(component);
        setModalIsOpen(true);
    };

    const tableColumns = useCatalogTableColumns({
        state,
        setState: updateState,
        catalogModel: state.catalogModel,
        openCatalogItemSidebar,
        cloneItem,
    });

    useEffect(() => {
        document.title = `Orderwerks >> Catalogs >> Catalog Overview`;

        const navItems = [
            { title: "catalogs", url: "/catalogs" },
            { title: "catalog_overview", url: `/catalogs/${catalogGuid}` },
        ];
        setNavItems(navItems);

        getCatalog();
        getPriceGroups();

        const qbdOptions = getQbdSelectableOptions();
        setQbdSelectableOptions(qbdOptions);
    }, [catalogGuid]);

    const getQbdSelectableOptions = () => {
        let options: any[] = [];
        if (supplierModel.Supplier_Integration.has_qbd) {
            if (supplierModel.Supplier_Integration.qbd_CatalogItems) {
                options = JSON.parse(supplierModel.Supplier_Integration.qbd_CatalogItems);
            }
        }
        return options;
    };

    const handleDeleteCatalogItem = useCallback(
        (item: ICatalog_ItemUI) => {
            const payload: IDeleteCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid,
                catalogItemGuid: item.guid,
            };

            socket.emit(EndpointsEnum.CatalogItemDeleteCatalogItem, payload);
            updateState({ sourceElem: null, sourceCatalogItem: undefined, deleteElem: null });
        },
        [catalogGuid]
    );

    const confirmDelete = useCallback(() => {
        if (state.sourceCatalogItem) {
            handleDeleteCatalogItem(state.sourceCatalogItem);
        }
    }, [state.sourceCatalogItem, handleDeleteCatalogItem]);

    const cancelDelete = () => {
        updateState({ sourceElem: null, sourceCatalogItem: undefined, deleteElem: null });
    };

    const filterCatalogItems = (queryString?: string, reset?: boolean, offset?: number) => {
        let finalQueryString = queryString;
        const newOffset = offset || state.offset;

        if (reset) {
            const newState: any = {
                offset: 0,
            };

            finalQueryString = `?order_dir=${state.sortDir}&order_by=name`;
            newState.queryString = finalQueryString;
            newState.sortDir = state.sortDir;
            newState.sortCol = "name";

            updateState(newState);
            // newOffset = 0;
        } else {
            // When performing a new search, reset offset to 0
            if (queryString !== undefined) {
                updateState({ queryString });
                finalQueryString = queryString;
                // newOffset = 0;
            } else {
                // If no queryString provided, use the one from state
                finalQueryString = state.queryString;
            }

            if (offset === 0) {
                updateState({ offset: 0 });
            }
        }

        getCatalogItems({ queryString: finalQueryString, limit: state.limit, offset: newOffset });
    };

    const setColumns = (columns: any) => {
        updateState({ columns });
    };

    const saveColumnsAsDefault = (columns: any) => {
        const orderCols: { [key: string]: boolean } = {};
        for (const prop of Object.keys(columns)) {
            orderCols[prop] = columns[prop].isChecked;
        }

        const currentToken = UserService.getCurrentToken(userModel);
        if (currentToken) {
            const payload = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                settings: {
                    catalog_cols: orderCols,
                },
                tokenGuid: currentToken.guid,
            };
            socket.emit(EndpointsEnum.TokenUpdateSettings, payload);
        }
    };

    const paginationChanged = (offset: number, limit: number) => {
        const updatedState = {
            ...state,
            offset,
            limit,
        };

        updateState(updatedState);
        getCatalogItems({ queryString: state.queryString, limit, offset });
    };

    const qbdDataMapperSave = (items: any[]) => {
        const payload = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            resource: "CATALOG_ITEM",
            items: _.map(items, (item) => {
                return {
                    resourceGuid: item.staticValue,
                    mapValues: {
                        qbd_ListID: item.selectedItem ? item.selectedItem.ListID : null,
                    },
                };
            }),
        };

        socket.emit(EndpointsEnum.QBDMapper, payload);
    };

    const qbdDataMapperCancel = () => {
        updateState({ qbdMapperVisible: false });
    };

    const toggleExtendedInventoryView = () => {
        updateState({ extendedInventoryView: !state.extendedInventoryView });
    };

    useEffect(() => {
        setContextState((prev) => ({
            ...prev,
            activeProcess: state.isDataFetching,
            queryString: state.queryString,
        }));
    }, [state.isDataFetching, state.queryString]);

    return (
        <CatalogContext.Provider value={contextState}>
            <div className="CatalogOverview position-relative">
                {state.catalog_items.length && !state.isLoading ? (
                    <OwDataMapper
                        isVisible={state.qbdMapperVisible || false}
                        saveFn={qbdDataMapperSave}
                        cancelFn={qbdDataMapperCancel}
                        mainTitle={"catalog_item_mapper"}
                        staticTitle={"catalog_items"}
                        staticOptions={state.catalog_items}
                        staticValueProp={"guid"}
                        staticValueSelectedProp={"qbd_ListID"}
                        staticTitleProp={"name"}
                        staticSubTitleProp={"sku"}
                        selectableTitle={"quickbooks_items"}
                        selectableOptions={qbdSelectableOptions}
                        selectableValueProp={"ListID"}
                        selectableTitleProp={"Name"}
                    />
                ) : null}

                <div>
                    <Breadcrumbs className={"d-inline-flex"} navItems={navItems} />
                    {state.catalogModel ? " - " + state.catalogModel.name : ""}
                </div>

                <div className="row">
                    <div className="col-12 d-flex align-items-center justify-items-center flex-wrap mb-2 mb-md-0 w-100">
                        <OwTitle title={"catalog_items"} leftIconName={"fad fa-books"} />
                        <div className={"mt-md-0 d-flex justify-content-between flex-grow"}>
                            <div className={"align-self-center"}>
                                {isReadOnly ? null : (
                                    <OwButton
                                        id={"create"}
                                        className={"btn-warning ms-md-3 me-2 me-md-0"}
                                        title={"create"}
                                        leftIconClass={"fas fa-plus"}
                                        fn={() => openCatalogItemSidebar()}
                                    />
                                )}
                            </div>
                            <div>
                                <OwToggle
                                    name={"extended_inventory_view"}
                                    label={"extended_inventory_view"}
                                    active={state.extendedInventoryView || false}
                                    fn={toggleExtendedInventoryView}
                                />
                            </div>
                        </div>
                        {uiContext.showHiddenFeatures && state.catalogModel && (
                            <div className="d-flex flex-column">
                                <div className="d-flex justify-content-start align-items-center">
                                    <b className="me-1">ID:</b> <ClickToCopy text={state.catalogModel.id.toString()} />
                                </div>
                                <div className="d-flex justify-content-start align-items-center">
                                    <b className="me-1">GUID:</b> <ClickToCopy text={state.catalogModel.guid} />
                                </div>
                            </div>
                        )}
                        {state.qbdItemSyncActive ? <QBDItemsSyncStatus /> : null}
                    </div>
                </div>

                <div className="mb-2">
                    <SearchSection>
                        <CatalogItemFilter
                            searchFn={filterCatalogItems}
                            sortCol={state.sortCol}
                            sortDir={state.sortDir}
                            limit={state.limit}
                            offset={state.offset}
                            emitOnLoad={true}
                        />
                    </SearchSection>
                </div>

                <div className={"row mt-1 mb-1"}>
                    <div className={"col-12"}>
                        <OwActionBar
                            togglePhotoView={(isPhotoView) => updateState({ isPhotoView })}
                            toggleSmallTable={(isSmallTable) => updateState({ isSmallTable, isPhotoView: false })}
                            colToggledFn={setColumns}
                            columns={state.columns}
                            actions={isReadOnly ? [] : actions}
                            totalValue={state.totalCatalogItems}
                            disabled={false}
                            saveAsDefaultsFn={saveColumnsAsDefault}
                        />
                    </div>
                </div>

                <div>
                    {state.showOrderRowConfig && state.catalogModel ? (
                        <OrderRowConfig
                            userModel={userModel}
                            supplierModel={supplierModel}
                            socket={socket}
                            catalogModel={state.catalogModel}
                            toggleOrderRowConfig={() => updateState({ showOrderRowConfig: false })}
                            notifyFn={uiContext.notifyFn}
                        />
                    ) : null}
                </div>

                <div>
                    {state.showInventoryRowConfig && state.catalogModel ? (
                        <InventoryRowConfig
                            userModel={userModel}
                            supplierModel={supplierModel}
                            socket={socket}
                            catalogModel={state.catalogModel}
                            toggleInventoryRowConfig={() => updateState({ showInventoryRowConfig: false })}
                            notifyFn={uiContext.notifyFn}
                        />
                    ) : null}
                </div>

                <div className={"row"}>
                    <div className={"col-12"}>
                        <div className="table-responsive position-relative">
                            <ConfirmDeleteOverlayFn
                                targetElem={state.deleteElem}
                                handleConfirm={confirmDelete}
                                handleCancel={cancelDelete}
                                message={"confirm_delete"}
                                nestedParentElem={"catalog-item-table"}
                                skipMainView={true}
                            />

                            <OwTable
                                id={"catalog-item-table"}
                                columns={tableColumns}
                                dataSet={state.catalog_items}
                                userModel={userModel}
                                supplierModel={supplierModel}
                                dataFetch={state.isDataFetching}
                                initialData={state.isInitialData}
                                currentSortCol={state.sortCol}
                                tableConfig={{
                                    tableStriped: true,
                                    tableSmall: state.isSmallTable,
                                    tableHasSort: true,
                                    alignHeadersMiddle: true,
                                }}
                                maintainTableHeight={true}
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className={"col-12"}>
                        {state.catalog_items.length ? (
                            <PaginationBar
                                offset={state.offset}
                                limit={state.limit}
                                totalRecords={state.totalCatalogItems}
                                onPaginationChanged={paginationChanged}
                            />
                        ) : null}
                    </div>
                </div>

                <div>
                    <Modal
                        isOpen={modalIsOpen}
                        onRequestClose={() => {
                            closeModal(true);
                        }}
                        contentLabel="Orderwerks"
                        shouldCloseOnOverlayClick={false}
                        className={"modalContentClass"}
                        overlayClassName={"modalOverlayClass"}>
                        {modalComponent}
                    </Modal>
                </div>
            </div>
        </CatalogContext.Provider>
    );
};

export default CatalogOverviewFn;
